"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.pasteSetRecords = void 0;
const command_manager_1 = require("../../command_manager");
const lodash_1 = require("lodash");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const field_types_1 = require("../../types/field_types");
const utils_1 = require("../../utils");
const __1 = require("..");
const add_records_1 = require("./add_records");
const set_records_1 = require("./set_records");
exports.pasteSetRecords = {
    undoable: true,
    execute: (context, options) => {
        const { model: state, fieldMapSnapshot } = context;
        const { recordIds, column, row, viewId, stdValues, cut, groupCellValues, notifyExistIncompatibleField } = options;
        const datasheetId = store_1.Selectors.getActiveDatasheetId(state);
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        const userInfo = state.user.info;
        if (!snapshot) {
            return null;
        }
        const view = (0, selectors_1.getViewById)(snapshot, viewId);
        // Currently only the Grid view has paste
        if (!view || ![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type)) {
            return null;
        }
        if (isNaN(column) || column < 0) {
            return null;
        }
        if (isNaN(row) || row < 0) {
            return null;
        }
        const fieldMap = snapshot.meta.fieldMap;
        const newRecordCount = stdValues.length;
        if (newRecordCount === 0) {
            return null;
        }
        const actions = [];
        // oi action for compensation when adding an alarm
        const defaultAlarmActions = [];
        // Add the oi action that the alarm clock actually takes effect
        const realAlarmActions = [];
        const linkedActions = [];
        const recordValues = [];
        // first delete the part that needs to be cut
        if (cut) {
            const datasheetId = cut.datasheetId;
            if (datasheetId === state.pageParams.datasheetId) {
                const cutRows = cut.rows;
                const cutColumns = cut.columns;
                cutRows.forEach(row => {
                    cutColumns.forEach(column => {
                        const field = fieldMap[column.fieldId];
                        if (field && field.type !== field_types_1.FieldType.NotSupport) {
                            recordValues.push({
                                recordId: row.recordId,
                                fieldId: column.fieldId,
                                value: null,
                            });
                        }
                    });
                });
            }
        }
        // There is already record paste assignment
        const columnCount = options.fields.length;
        const visibleColumns = (0, selectors_1.getVisibleColumns)(state);
        const columnsToPaste = visibleColumns.slice(column, column + columnCount);
        if (columnsToPaste.length === 0) {
            return null;
        }
        const recordIdsToPaste = (0, selectors_1.getRangeRows)(state, row, row + newRecordCount).map(r => r.recordId);
        function addAlarm(cv, field, recordId, oldRecordId) {
            var _a;
            if (field.type === field_types_1.FieldType.DateTime && cv && snapshot) {
                const alarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, oldRecordId, field.id);
                if (alarm) {
                    const curAlarmActions = model_1.DatasheetActions.setDateTimeCellAlarm(snapshot, {
                        recordId,
                        fieldId: field.id,
                        alarm: Object.assign(Object.assign({ id: (0, utils_1.getNewId)(utils_1.IDPrefix.DateTimeAlarm) }, (0, lodash_1.omit)(alarm, 'id')), { 
                            // If the copy is another member, the alarm set by others will be changed to himself
                            alarmUsers: userInfo && ((_a = alarm.alarmUsers) === null || _a === void 0 ? void 0 : _a[0].type) === store_1.AlarmUsersType.Member ? [{
                                    type: store_1.AlarmUsersType.Member,
                                    data: userInfo.unitId,
                                }] : alarm.alarmUsers }),
                    });
                    if (curAlarmActions) {
                        // there is a compensated OP
                        if (curAlarmActions.length === 2) {
                            /**
                             * p same merge (merge of peers into one oi action)
                             * p is not the same, not merged (different lines are oi action alone)
                             */
                            let isMerged = false;
                            defaultAlarmActions.forEach((action, idx) => {
                                if ((0, lodash_1.isEqual)(action.p, curAlarmActions[0].p)) {
                                    defaultAlarmActions[idx].oi = Object.assign(Object.assign({}, curAlarmActions[0].oi), action.oi);
                                    isMerged = true;
                                }
                            });
                            if (!isMerged) {
                                defaultAlarmActions.push(curAlarmActions[0]);
                            }
                            realAlarmActions.push(curAlarmActions[1]);
                        }
                        else {
                            realAlarmActions.push(...curAlarmActions);
                        }
                    }
                }
            }
        }
        function pushPasteValue(stdValue, field, recordId, oldRecordId) {
            if (!field || field.type === field_types_1.FieldType.NotSupport) {
                return;
            }
            let value = model_1.Field.bindContext(field, state).stdValueToCellValue(stdValue);
            value = (0, model_1.handleEmptyCellValue)(value, model_1.Field.bindContext(field, state).basicValueType);
            if (value == null && stdValue.data.length && field.type !== stdValue.sourceType) {
                // This indicates that there is a mismatched type
                notifyExistIncompatibleField === null || notifyExistIncompatibleField === void 0 ? void 0 : notifyExistIncompatibleField();
            }
            recordValues.push({
                fieldId: field.id,
                recordId,
                value,
            });
            // Paste the cell as a date type, with a value and with an alarm
            oldRecordId && addAlarm(value, field, recordId, oldRecordId);
        }
        // In the case where only one cell is copied, paste over the selection
        const singleCellPaste = stdValues.length === 1 && stdValues[0].length === 1;
        if (singleCellPaste) {
            const ranges = (0, selectors_1.getSelectRanges)(state);
            const range = ranges[0];
            const rows = store_1.Selectors.getRangeRecords(state, range);
            const fields = store_1.Selectors.getRangeFields(state, range, datasheetId);
            if (!rows || !fields) {
                return null;
            }
            for (const row of rows) {
                const recordId = row.recordId;
                for (const field of fields) {
                    pushPasteValue(stdValues[0][0], field, recordId, recordIds === null || recordIds === void 0 ? void 0 : recordIds[0]);
                }
            }
            // When multiple cells are copied, the negative value area shall prevail, the selection area is irrelevant
        }
        else {
            for (let i = 0; stdValues[i] && recordIdsToPaste[i]; i++) {
                const recordId = recordIdsToPaste[i];
                const stdValuesRow = stdValues[i];
                for (let c = 0; stdValuesRow[c] && columnsToPaste[c]; c++) {
                    const fieldId = columnsToPaste[c].fieldId;
                    const stdValue = stdValuesRow[c];
                    const field = fieldMap[fieldId];
                    pushPasteValue(stdValue, field, recordId, recordIds === null || recordIds === void 0 ? void 0 : recordIds[i]);
                }
            }
        }
        const rst = set_records_1.setRecords.execute(context, { cmd: __1.CollaCommandName.SetRecords, data: recordValues });
        if (rst) {
            if (rst.result === command_manager_1.ExecuteResult.Fail) {
                return rst;
            }
            Object.assign(fieldMapSnapshot, rst.fieldMapSnapshot || {});
            actions.push(...rst.actions);
        }
        // Add new line and paste assignment for augmented line
        const newStdValues = stdValues.slice(recordIdsToPaste.length);
        if (newStdValues.length) {
            const recordValues = [];
            const oldRecordIds = [];
            for (let row = 0; newStdValues[row]; row++) {
                const stdValuesRow = newStdValues[row];
                const cellValues = {};
                // Convert a line to a record
                for (let column = 0; stdValuesRow[column] && columnsToPaste[column]; column++) {
                    const fieldId = columnsToPaste[column].fieldId;
                    const stdValue = stdValuesRow[column];
                    const field = fieldMap[fieldId];
                    if (!field || field.type === field_types_1.FieldType.NotSupport) {
                        continue;
                    }
                    let value = model_1.Field.bindContext(field, state).stdValueToCellValue(stdValue);
                    value = (0, model_1.handleEmptyCellValue)(value, model_1.Field.bindContext(field, state).basicValueType);
                    cellValues[fieldId] = value;
                }
                recordValues.push(cellValues);
                recordIds && oldRecordIds.push(recordIds[recordIdsToPaste.length + row]);
            }
            const rst = add_records_1.addRecords.execute(context, {
                cmd: __1.CollaCommandName.AddRecords,
                viewId,
                index: (0, selectors_1.getActualRowCount)(state),
                count: newStdValues.length,
                groupCellValues,
                cellValues: recordValues,
            });
            if (rst) {
                if (rst.result === command_manager_1.ExecuteResult.Fail) {
                    return rst;
                }
                Object.assign(fieldMapSnapshot, rst.fieldMapSnapshot || {});
                actions.push(...rst.actions);
                const newRecordIds = rst.data;
                recordValues.forEach((rv, cvIndex) => {
                    Object.keys(rv).forEach(fId => {
                        addAlarm(rv[fId], fieldMap[fId], newRecordIds[cvIndex], oldRecordIds[cvIndex]);
                    });
                });
            }
        }
        if (realAlarmActions.length > 0) {
            actions.push(...defaultAlarmActions);
            actions.push(...realAlarmActions);
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions,
            linkedActions,
            fieldMapSnapshot
        };
    },
};
//# sourceMappingURL=paste_set_records.js.map